import { Moon, Sunrise } from "lucide-react"
import type { ReactNode } from "react"
import { sleepTypeMeta } from "@/features/sleep/sleep.config"
import { cn } from "@/shared/lib/utils"
import { formatClock } from "@/shared/utils/time"
import type { Dashboard, DashboardSession } from "./api/dashboard.queries"
import {
  ARC,
  type ArcWindow,
  arcPath,
  buildArcSegments,
  fractionToAngle,
  insetForCap,
  ongoingWrapEnd,
  pointAtRadius,
  roundCoord,
  sleepLabels,
  timeToFraction,
} from "./dashboard.config"

// Per-star twinkle delay/duration keep the blinking out of sync.
const STARS = [
  { cx: 60, cy: 50, r: 1.2, delay: 0, dur: 3.2 },
  { cx: 120, cy: 30, r: 1, delay: 1.4, dur: 4.1 },
  { cx: 200, cy: 45, r: 1.4, delay: 0.7, dur: 3.6 },
  { cx: 250, cy: 80, r: 1, delay: 2.1, dur: 4.4 },
  { cx: 40, cy: 110, r: 1, delay: 1.1, dur: 3.9 },
  { cx: 270, cy: 130, r: 1.2, delay: 2.6, dur: 3.3 },
  { cx: 90, cy: 90, r: 0.9, delay: 0.4, dur: 4.2 },
  { cx: 170, cy: 65, r: 1, delay: 1.8, dur: 3.5 },
  { cx: 230, cy: 160, r: 0.9, delay: 2.3, dur: 4 },
  { cx: 150, cy: 120, r: 1.1, delay: 0.9, dur: 3.7 },
]

const HALF = ARC.strokeWidth / 2
const R_OUT = ARC.radius + HALF
const R_IN = ARC.radius - HALF
const LABEL_RADIUS = ARC.radius + HALF + 13

// Closed outline of a rounded band segment so a dashed stroke wraps the whole capsule.
function capsuleOutline(t0: number, t1: number): string {
  const a0 = fractionToAngle(t0)
  const a1 = fractionToAngle(t1)
  const largeArc = a1 - a0 > 180 ? 1 : 0
  const o0 = pointAtRadius(a0, R_OUT)
  const o1 = pointAtRadius(a1, R_OUT)
  const i0 = pointAtRadius(a0, R_IN)
  const i1 = pointAtRadius(a1, R_IN)
  return [
    `M${roundCoord(o0.x)},${roundCoord(o0.y)}`,
    `A${R_OUT},${R_OUT} 0 ${largeArc} 1 ${roundCoord(o1.x)},${roundCoord(o1.y)}`,
    `A${HALF},${HALF} 0 0 1 ${roundCoord(i1.x)},${roundCoord(i1.y)}`,
    `A${R_IN},${R_IN} 0 ${largeArc} 0 ${roundCoord(i0.x)},${roundCoord(i0.y)}`,
    `A${HALF},${HALF} 0 0 1 ${roundCoord(o0.x)},${roundCoord(o0.y)}`,
    "Z",
  ].join(" ")
}

// Percent position of a viewBox point, for HTML overlays over the scaled SVG.
function overlayStyle(x: number, y: number) {
  return { left: `${(x / ARC.width) * 100}%`, top: `${(y / ARC.height) * 100}%` }
}

function pointStyle(fraction: number, radius: number) {
  const p = pointAtRadius(fractionToAngle(fraction), radius)
  return overlayStyle(p.x, p.y)
}

// Rotate a label to follow the arc tangent, no upright-flip (upside-down at the
// very bottom is intentional — baseline always faces the band).
function tangentRotation(fraction: number): number {
  return (fractionToAngle(fraction) + 90) % 360
}

interface DayArcProps {
  dashboard: Dashboard
  now: Date
  window: ArcWindow
  center: ReactNode
  onSelectSession: (session: DashboardSession) => void
}

export function DayArc({ dashboard, now, window, center, onSelectSession }: DayArcProps) {
  const { predictions, todaySessions } = dashboard
  const { startMs, endMs, wakeMs, bedtimeMs, mode } = window
  const nowMs = now.getTime()

  const fraction = (ms: number) => timeToFraction(ms, startMs, endMs)

  const segments = buildArcSegments(todaySessions, startMs, endMs, nowMs)
  const timeLabels = segments.flatMap(sleepLabels)

  // Next-nap prediction is a daytime concept only.
  const napAt = predictions.nextNapAt ? new Date(predictions.nextNapAt).getTime() : null
  const napDuration = predictions.nextNapDurationMinutes
  const napSegment =
    mode === "day" && napAt !== null && napDuration
      ? { t0: fraction(napAt), t1: fraction(napAt + napDuration * 60_000) }
      : null

  // Sunrise marks the wake endpoint, moon the bedtime endpoint; the window places
  // each at fraction 0 or 1, so their positions follow without re-checking mode.
  const sunrisePt = pointAtRadius(fractionToAngle(fraction(wakeMs)), ARC.radius)
  const moonPt = pointAtRadius(fractionToAngle(fraction(bedtimeMs)), ARC.radius)
  const nowPt = pointAtRadius(fractionToAngle(fraction(nowMs)), ARC.radius)

  return (
    <div className="relative mx-auto aspect-[300/290] w-full max-w-sm">
      <svg
        viewBox={`0 0 ${ARC.width} ${ARC.height}`}
        className="block h-auto w-full"
        role="img"
        aria-label="Sömnbåge för dygnet"
      >
        <title>Sömnbåge för dygnet</title>
        <g className="fill-accent">
          {STARS.map((star) => (
            <circle
              key={`${star.cx}-${star.cy}`}
              cx={star.cx}
              cy={star.cy}
              r={star.r}
              className="animate-star-twinkle"
              style={{ animationDelay: `${star.delay}s`, animationDuration: `${star.dur}s` }}
            />
          ))}
        </g>

        {/* base track */}
        <path
          d={arcPath(0, 1)}
          fill="none"
          className="stroke-secondary"
          strokeWidth={ARC.strokeWidth}
          strokeLinecap="round"
          opacity={0.4}
        />

        {/* predicted next nap (dotted) */}
        {napSegment && napSegment.t1 > napSegment.t0 && (
          <path
            d={arcPath(napSegment.t0, napSegment.t1)}
            fill="none"
            className="stroke-accent"
            strokeWidth={ARC.strokeWidth}
            strokeLinecap="round"
            strokeDasharray="1 14"
          />
        )}

        {/* Visual only — tap target is the midpoint icon-button overlay below,
            since SVG can't host a semantic <button>. */}
        {segments.map((s) => {
          const [d0, d1] = insetForCap(s.t0, s.t1)
          // Ongoing session: extend the dashed outline past `now` so it wraps the
          // now-marker. The solid fill stays at `now` — elapsed area stays honest.
          const outlineEnd = s.session.endTime ? d1 : ongoingWrapEnd(s.t1)
          return (
            <g key={s.id}>
              <path
                d={arcPath(d0, d1)}
                fill="none"
                className="stroke-primary"
                strokeWidth={ARC.strokeWidth}
                strokeLinecap="round"
                opacity={0.22}
              />
              <path
                d={capsuleOutline(d0, outlineEnd)}
                fill="none"
                className="stroke-primary"
                strokeWidth={1}
                strokeDasharray="4 4"
                opacity={0.9}
              />
            </g>
          )
        })}
      </svg>

      {/* Segment-midpoint type icon: tap target opening the edit dialog. */}
      {segments.map((s) => {
        const meta = sleepTypeMeta(s.type)
        const Icon = meta.icon
        return (
          <button
            key={`icon-${s.id}`}
            type="button"
            onClick={() => onSelectSession(s.session)}
            aria-label={`Redigera ${meta.label}`}
            className="-translate-x-1/2 -translate-y-1/2 absolute flex size-8 items-center justify-center rounded-full text-primary"
            style={pointStyle((s.t0 + s.t1) / 2, ARC.radius)}
          >
            <Icon className="size-4" />
          </button>
        )
      })}

      {timeLabels.map((l) => (
        <div
          key={`${l.fraction}-${l.text}`}
          className="absolute font-medium text-[11px] text-muted-foreground tabular-nums"
          style={{
            ...pointStyle(l.fraction, LABEL_RADIUS),
            transform: `translate(-50%, -50%) rotate(${tangentRotation(l.fraction)}deg)`,
          }}
        >
          {l.text}
        </div>
      ))}

      {/* Sunrise (wake): bottom-left by day, bottom-right at night. Raw amber intentional — no semantic token fits the marker. */}
      <div
        className="-translate-x-1/2 -translate-y-1/2 absolute"
        style={overlayStyle(sunrisePt.x, sunrisePt.y)}
      >
        <span className="flex size-8 items-center justify-center rounded-full border-2 border-amber-300 bg-card shadow-[0_0_26px_8px_rgba(252,211,77,0.2)]">
          <Sunrise className="size-4 text-amber-300 drop-shadow-[0_0_7px_rgba(252,211,77,0.48)]" />
        </span>
        <span className="-translate-x-1/2 absolute top-full left-1/2 mt-1 whitespace-nowrap font-normal text-amber-300 text-sm">
          {formatClock(new Date(wakeMs))}
        </span>
      </div>
      {/* Moon (bedtime): bottom-right by day, bottom-left at night. */}
      <div
        className="-translate-x-1/2 -translate-y-1/2 absolute"
        style={overlayStyle(moonPt.x, moonPt.y)}
      >
        <span className="flex size-8 items-center justify-center rounded-full border border-amber-400 border-dashed bg-amber-400/10">
          <Moon className="size-4 text-amber-400" />
        </span>
        <span className="-translate-x-1/2 absolute top-full left-1/2 mt-1 whitespace-nowrap font-normal text-amber-400 text-sm">
          {formatClock(new Date(bedtimeMs))}
        </span>
      </div>

      <div
        className={cn("-translate-x-1/2 -translate-y-1/2 absolute flex w-48 flex-col items-center")}
        style={overlayStyle(ARC.cx, ARC.cy)}
      >
        {center}
      </div>

      {/* now marker — own top layer so it never sits under an icon/timestamp/endpoint. */}
      <svg
        viewBox={`0 0 ${ARC.width} ${ARC.height}`}
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <circle cx={nowPt.x} cy={nowPt.y} r={HALF + 3} className="fill-foreground" opacity={0.18} />
        <circle cx={nowPt.x} cy={nowPt.y} r={HALF} className="fill-foreground" />
      </svg>
    </div>
  )
}
